/**
 * Plattformverwaltung.
 *
 * Jeder Aufruf hier greift betriebsübergreifend zu und wird serverseitig
 * protokolliert. Die Oberfläche macht das sichtbar, statt es zu verstecken —
 * wer hier arbeitet, soll wissen, dass es nachvollziehbar ist.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { oneOf, usePersistentState } from '../lib/persistent-state'

import {
  api,
  ApiError,
  type AuditEntry,
  type ListResponse,
  type Me,
  type TenantOverview,
  type UserOverview,
} from '../lib/api'
import { AUDIT_LABELS } from '../lib/audit-labels'
import { Banner, Button, Card, Chip, Dialog, Spinner, TextField, useToast } from '../ui'

const TABS = ['betriebe', 'nutzer', 'protokoll'] as const
type Tab = (typeof TABS)[number]

export function AdminPage({ me }: { me: Me }) {
  const [tab, setTab] = usePersistentState<Tab>('admin.tab', 'betriebe', oneOf(TABS))

  return (
    <div className="stack">
      <div className="row row--between">
        <h1>Verwaltung</h1>
        <Chip tone="warning">betriebsübergreifend · wird protokolliert</Chip>
      </div>

      <div className="row" role="tablist" aria-label="Bereiche">
        {(
          [
            ['betriebe', 'Betriebe'],
            ['nutzer', 'Nutzer'],
            ['protokoll', 'Protokoll'],
          ] as [Tab, string][]
        ).map(([value, label]) => (
          <Button
            key={value}
            variant={tab === value ? 'tonal' : 'text'}
            small
            role="tab"
            aria-selected={tab === value}
            onClick={() => setTab(value)}
          >
            {label}
          </Button>
        ))}
      </div>

      {tab === 'betriebe' && <TenantsTab />}
      {tab === 'nutzer' && <UsersTab me={me} />}
      {tab === 'protokoll' && <AuditTab />}
    </div>
  )
}

// --------------------------------------------------------------- Betriebe ---

function TenantsTab() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [createOpen, setCreateOpen] = useState(false)

  const tenants = useQuery({
    queryKey: ['admin-tenants'],
    queryFn: () => api.get<ListResponse<TenantOverview>>('/api/admin/tenants'),
  })

  const deactivate = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/tenants/${id}/deactivate`),
    onSuccess: () => {
      toast('Betrieb stillgelegt. Die Daten bleiben erhalten.', 'success')
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  if (tenants.isPending) return <Spinner />

  return (
    <>
      <Card
        title={`Betriebe (${tenants.data?.total ?? 0})`}
        action={<Button small onClick={() => setCreateOpen(true)}>Betrieb anlegen</Button>}
      >
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Kürzel</th>
                <th>Personen</th>
                <th>Status</th>
                <th aria-label="Aktionen" />
              </tr>
            </thead>
            <tbody>
              {tenants.data?.items.map((tenant) => (
                <tr key={tenant.tenant_id}>
                  <td data-label="Name">{tenant.name}</td>
                  <td data-label="Kürzel" className="muted">
                    <code>{tenant.slug}</code>
                  </td>
                  <td data-label="Personen">{tenant.member_count}</td>
                  <td data-label="Status">
                    {tenant.is_active ? (
                      <Chip tone="success">aktiv</Chip>
                    ) : (
                      <Chip>stillgelegt</Chip>
                    )}
                  </td>
                  <td data-label="Aktion">
                    {tenant.is_active && (
                      <Button
                        variant="text"
                        small
                        onClick={() => {
                          if (window.confirm(`„${tenant.name}" stilllegen?`)) {
                            deactivate.mutate(tenant.tenant_id)
                          }
                        }}
                      >
                        Stilllegen
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <CreateTenantDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  )
}

function CreateTenantDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [name, setName] = useState('')
  const [ownerEmail, setOwnerEmail] = useState('')
  const [error, setError] = useState<string | null>(null)

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/admin/tenants', {
        name,
        owner_email: ownerEmail.trim() === '' ? null : ownerEmail.trim(),
      }),
    onSuccess: () => {
      toast(`„${name}" angelegt.`, 'success')
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] })
      setName('')
      setOwnerEmail('')
      setError(null)
      onClose()
    },
    onError: (err) => setError((err as ApiError).message),
  })

  return (
    <Dialog open={open} onClose={onClose} title="Betrieb anlegen">
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault()
          setError(null)
          create.mutate()
        }}
      >
        {error && <Banner tone="error">{error}</Banner>}
        <TextField
          label="Name"
          required
          minLength={2}
          value={name}
          onChange={(event) => setName(event.target.value)}
          hint="Das Kürzel wird daraus abgeleitet."
        />
        <TextField
          label="Inhaber (E-Mail)"
          type="email"
          value={ownerEmail}
          onChange={(event) => setOwnerEmail(event.target.value)}
          hint="Optional. Die Person braucht bereits ein Konto."
        />
        <div className="row row--between">
          <Button variant="text" type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" busy={create.isPending}>
            Anlegen
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

// ----------------------------------------------------------------- Nutzer ---

function UsersTab({ me }: { me: Me }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [search, setSearch] = useState('')

  const users = useQuery({
    queryKey: ['admin-users', search],
    queryFn: () =>
      api.get<ListResponse<UserOverview>>(
        `/api/admin/users${search ? `?search=${encodeURIComponent(search)}` : ''}`,
      ),
  })

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.post(`/api/admin/users/${id}/active`, { active }),
    onSuccess: (_data, variables) => {
      toast(variables.active ? 'Konto entsperrt.' : 'Konto gesperrt und abgemeldet.', 'success')
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  const impersonate = useMutation({
    mutationFn: (id: string) => api.post('/api/admin/impersonate', { user_id: id }),
    onSuccess: () => {
      // Identitätswechsel: alles neu laden, sonst zeigt der Zwischenspeicher
      // die Daten der falschen Person.
      window.location.assign('/')
    },
    onError: (error) => toast((error as ApiError).message, 'error'),
  })

  return (
    <Card title="Nutzer">
      <div className="stack">
        <TextField
          label="Suchen"
          placeholder="Name oder E-Mail"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {users.isPending ? (
          <Spinner />
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>E-Mail</th>
                  <th>Betriebe</th>
                  <th>Status</th>
                  <th aria-label="Aktionen" />
                </tr>
              </thead>
              <tbody>
                {users.data?.items.map((user) => (
                  <tr key={user.user_id}>
                    <td data-label="Name">
                      {user.full_name}
                      {user.is_superadmin && (
                        <>
                          {' '}
                          <Chip tone="primary">Superadmin</Chip>
                        </>
                      )}
                    </td>
                    <td data-label="E-Mail" className="muted">
                      {user.email}
                    </td>
                    <td data-label="Betriebe">{user.tenant_count}</td>
                    <td data-label="Status">
                      {!user.is_active ? (
                        <Chip tone="danger">gesperrt</Chip>
                      ) : user.email_verified ? (
                        <Chip tone="success">aktiv</Chip>
                      ) : (
                        <Chip tone="warning">unbestätigt</Chip>
                      )}
                    </td>
                    <td data-label="Aktion">
                      <span className="row">
                        {user.user_id !== me.id && !user.is_superadmin && (
                          <>
                            <Button
                              variant="text"
                              small
                              onClick={() =>
                                setActive.mutate({ id: user.user_id, active: !user.is_active })
                              }
                            >
                              {user.is_active ? 'Sperren' : 'Entsperren'}
                            </Button>
                            {user.is_active && (
                              <Button
                                variant="text"
                                small
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      `Sitzung von ${user.full_name} übernehmen? ` +
                                        'Der Vorgang wird protokolliert und endet nach einer Stunde.',
                                    )
                                  ) {
                                    impersonate.mutate(user.user_id)
                                  }
                                }}
                              >
                                Übernehmen
                              </Button>
                            )}
                          </>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  )
}

// -------------------------------------------------------------- Protokoll ---

function AuditTab() {
  const audit = useQuery({
    queryKey: ['admin-audit'],
    queryFn: () => api.get<ListResponse<AuditEntry>>('/api/admin/audit?limit=200'),
  })

  if (audit.isPending) return <Spinner />

  return (
    <Card title="Protokoll" action={<Chip>letzte 200 Einträge</Chip>}>
      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Zeitpunkt</th>
              <th>Vorgang</th>
              <th>Betrieb</th>
              <th>Ergebnis</th>
            </tr>
          </thead>
          <tbody>
            {audit.data?.items.map((entry) => (
              <tr key={entry.id}>
                <td data-label="Zeitpunkt" className="muted">
                  {new Date(entry.created_at).toLocaleString('de-DE', {
                    dateStyle: 'short',
                    timeStyle: 'medium',
                  })}
                </td>
                <td data-label="Vorgang">
                  {AUDIT_LABELS[entry.action] ?? entry.action}
                  {entry.impersonator_user_id && (
                    <>
                      {' '}
                      <Chip tone="warning">in fremdem Namen</Chip>
                    </>
                  )}
                </td>
                <td data-label="Betrieb" className="muted">
                  {entry.tenant_id ? entry.tenant_id.slice(0, 8) : 'plattformweit'}
                </td>
                <td data-label="Ergebnis">
                  {entry.result === 'success' ? (
                    <Chip tone="success">erfolgreich</Chip>
                  ) : (
                    <Chip tone="danger">gescheitert</Chip>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
